import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { FeedbackDocument, FeedbackType, FeedbackConstraints } from '@syfo/types/feedback.types';
import { PrimaryButton } from '@/components/ui/primary-button';
import { SecondaryButton } from '@/components/ui/secondary-button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { submitFeedbackThunk } from '@/stores/thunks/submit-feedback-thunk';
import { AppDispatch } from '@/stores/store';
import { SuccessIcon } from '@/components/icons/success-icon';

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Dialog component for submitting user feedback
 */
export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ 
  isOpen,
  onClose
}) => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Form state
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(FeedbackType.SUGGESTION);
  const [content, setContent] = useState('');
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string>('');

  // Reset form state when dialog closes
  React.useEffect(() => {
    if (!isOpen) {
      // Small delay to avoid visible reset while closing
      const timer = setTimeout(() => {
        setFeedbackType(FeedbackType.SUGGESTION);
        setContent('');
        setShowSuccess(false);
        setError('');
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Validation
    if (!content.trim()) {
      setError('Please enter your feedback');
      return;
    }

    if (content.length > FeedbackConstraints.document.content.maxLength) {
      setError(`Feedback must be ${FeedbackConstraints.document.content.maxLength} characters or less`);
      return;
    }

    setIsSubmitting(true);
    setError('');
    
    try {
      const feedback: FeedbackDocument = {
        type: feedbackType,
        content: content.trim()
      };

      await dispatch(submitFeedbackThunk({ feedback })).unwrap();
      
      // Show success message
      setShowSuccess(true);
      setContent('');
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Handle close after success
  const handleSuccessClose = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-[500px]" onOpenAutoFocus={(e) => e.preventDefault()} onInteractOutside={ e => e.preventDefault() }>
        {!showSuccess ? (
          <>
            <DialogHeader>
              <DialogTitle>Send Feedback</DialogTitle>
              <DialogDescription>
                Help us improve SYFO by sharing your thoughts, reporting issues, or suggesting new features.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Feedback Type */}
              <div className="space-y-2">
                <Label htmlFor="feedback-type">Type</Label>
                <NativeSelect
                  id="feedback-type"
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
                  disabled={isSubmitting}
                >
                  <NativeSelectOption value={FeedbackType.BUG}>Bug Report</NativeSelectOption>
                  <NativeSelectOption value={FeedbackType.SUGGESTION}>Suggestion</NativeSelectOption>
                  <NativeSelectOption value={FeedbackType.COMPLIMENT}>Compliment</NativeSelectOption>
                  <NativeSelectOption value={FeedbackType.OTHER}>Other</NativeSelectOption>
                </NativeSelect>
              </div>

              {/* Feedback Content */}
              <div className="space-y-2">
                <Label htmlFor="feedback-content">
                  Your Feedback
                  <span className="text-xs text-muted-foreground ml-2">
                    ({content.length}/{FeedbackConstraints.document.content.maxLength})
                  </span>
                </Label>
                <Textarea
                  id="feedback-content"
                  value={content}
                  onChange={(e) => {
                    setContent(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="Tell us what's on your mind..."
                  rows={6}
                  disabled={isSubmitting}
                  maxLength={FeedbackConstraints.document.content.maxLength}
                />
                {error && (
                  <p className="text-sm text-destructive">{error}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-4">
                <SecondaryButton
                  type="button"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={isSubmitting || !content.trim()}
                >
                  {isSubmitting ? 'Sending...' : 'Send Feedback'}
                </PrimaryButton>
              </div>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <SuccessIcon className="h-5 w-5 text-green-600" />
                Thanks for your feedback!
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-muted-foreground">
                Your feedback has been received. We appreciate you taking the time to help us improve SYFO.
              </p>
            </div>

            <div className="flex justify-end">
              <PrimaryButton onClick={handleSuccessClose}>
                Close
              </PrimaryButton>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
